/* Photo annotator — mark up a rejected photo before it becomes a corrective task.
 *
 * The job is to say WHERE the problem is: circle the gap on the shelf, box the wrong facing, write
 * a word next to it. Four marks cover that — pen, box, arrow, text — plus a stroke weight and a
 * colour. Anything more is a drawing app, not a review tool.
 *
 * Two things shape the implementation:
 *
 * 1. The viewport is FIXED height and the photo is fitted into it. A shelf photo is 3–4000px tall;
 *    rendered at the form's width it pushed every field below the fold, which is what made the
 *    form unusable. Scroll zooms about the cursor and drag pans, so detail is still reachable.
 * 2. Marks are stored in the IMAGE's coordinate space, never the viewport's. That is what lets the
 *    view zoom, pan and resize without the marks drifting, and what lets the export run at the
 *    photo's own resolution from a second, untransformed stage.
 *
 * The photo is loaded through `images/convert_image_to_base64/` — the same server-side hop
 * `urlToIdPhotoAsync` uses — because the S3 bucket sends no CORS headers, and a canvas that has
 * drawn a cross-origin image is tainted: `toDataURL()` then throws and there is nothing to upload.
 */
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Alert as AntAlert, Button, Flex, Segmented, Skeleton, Space, Tooltip, Typography } from "antd";
import { AimOutlined, ArrowRightOutlined, BorderOutlined, ClearOutlined, DragOutlined, EditOutlined, FontSizeOutlined, UndoOutlined } from "@ant-design/icons";
import { Arrow, Image as KonvaImage, Layer, Line, Rect, Stage, Text } from "react-konva";

import { PHOTO_REPORT } from "../config/lang";
import { callAsync } from "../util/action";
import { translate } from "../util/util";

const TOOLS = ["pen", "rect", "arrow", "text", "pan"];
const TOOL_ICON = {
  pen: <EditOutlined />,
  rect: <BorderOutlined />,
  arrow: <ArrowRightOutlined />,
  text: <FontSizeOutlined />,
  pan: <DragOutlined />,
};
/** Stroke weights, in image pixels — a 2px line is invisible on a 3000px photo, so these scale. */
const WEIGHTS = { s: 0.004, m: 0.008, l: 0.016 };
const MIN_SCALE = 0.1;
const MAX_SCALE = 8;
/** Caps the exported bitmap. A 6000px source would otherwise allocate a 144MB canvas. */
const MAX_EXPORT_WIDTH = 2400;

const readToken = name =>
  getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

/** The two ends of a mark; freehand keeps everything between them. */
const ends = points => [points[0], points[1], points.at(-2), points.at(-1)];

/** Every mark, drawn the same way in the live stage and in the export stage. `onMoveText` is passed
    only for the live stage — the export stage is a static snapshot and must not be interactive. */
function Marks({ shapes, onMoveText }) {
  return shapes.map((shape, i) => {
    const [x1, y1, x2, y2] = ends(shape.points);
    /* Text is sized off the stroke weight so one control drives both, but at 5× rather than 3×:
       at 3× an M label on a 1920px photo came out at 45 image px, which is 9px on screen once the
       photo is fitted — legible in the export, unreadable while placing it.
       A placed label is draggable so it can be nudged off whatever it is pointing at. */
    if (shape.tool === "text")
      return (
        <Text
          key={i}
          name="mark-text"
          x={x1}
          y={y1}
          text={shape.text}
          fill={shape.stroke}
          fontSize={shape.weight * 5}
          fontStyle="bold"
          lineHeight={1.2}
          draggable={!!onMoveText}
          onDragEnd={onMoveText ? event => onMoveText(i, event.target.x(), event.target.y()) : undefined}
          onMouseEnter={onMoveText ? event => (event.target.getStage().container().style.cursor = "move") : undefined}
          onMouseLeave={onMoveText ? event => (event.target.getStage().container().style.cursor = "") : undefined}
        />
      );
    if (shape.tool === "pen")
      return <Line key={i} points={shape.points} stroke={shape.stroke} strokeWidth={shape.weight} lineCap="round" lineJoin="round" tension={0.4} />;
    if (shape.tool === "rect") return <Rect key={i} x={x1} y={y1} width={x2 - x1} height={y2 - y1} stroke={shape.stroke} strokeWidth={shape.weight} />;
    return (
      <Arrow
        key={i}
        points={[x1, y1, x2, y2]}
        stroke={shape.stroke}
        fill={shape.stroke}
        strokeWidth={shape.weight}
        pointerLength={shape.weight * 3}
        pointerWidth={shape.weight * 3}
      />
    );
  });
}

function PhotoAnnotator({ src, lang, onDirtyChange }, ref) {
  const t = key => translate(PHOTO_REPORT, key, lang);

  const [image, setImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [tool, setTool] = useState("pen");
  const [weightKey, setWeightKey] = useState("m");
  const [colour, setColour] = useState(null);
  const [shapes, setShapes] = useState([]);
  /** `{ x, y, value }` in image space while a text mark is being typed. */
  const [typing, setTyping] = useState(null);
  const [view, setView] = useState({ scale: 1, x: 0, y: 0 });
  const [box, setBox] = useState({ width: 0, height: 0 });

  const drawing = useRef(false);
  const exportStage = useRef(null);
  const typingRef = useRef(null);
  const observer = useRef(null);
  /* The click that CREATES a text box also fires a blur on the freshly-focused input (the pointer
     sequence steals focus straight back). Committing on that blur closes the empty box before a
     key is pressed. Arm the commit-on-blur only after the creating click settles. */
  const armed = useRef(false);

  const palette = useMemo(() => [readToken("--danger-500"), readToken("--warning-500"), readToken("--success-700"), readToken("--action-primary")], []);
  const stroke = colour || palette[0];
  /* Weights are a fraction of the photo's long edge, so a mark reads the same on any resolution. */
  const weight = image ? Math.max(2, Math.round(Math.max(image.width, image.height) * WEIGHTS[weightKey])) : 4;

  /* Fetch through the API so the pixels are same-origin by the time they reach the canvas. */
  useEffect(() => {
    let alive = true;
    setLoading(true);
    setFailed(false);
    callAsync({ method: "POST", url: "images/convert_image_to_base64/", post: { image: src } }, data => {
      if (!alive) return;
      if (!data?.image) {
        setFailed(true);
        setLoading(false);
        return;
      }
      const element = new window.Image();
      element.onload = () => alive && (setImage(element), setLoading(false));
      element.onerror = () => alive && (setFailed(true), setLoading(false));
      /* The endpoint returns bare base64 for some records and a full data URI for others. */
      element.src = String(data.image).startsWith("data:") ? data.image : `data:image/jpeg;base64,${data.image}`;
    });
    return () => {
      alive = false;
    };
  }, [src]);

  /**
   * Konva sizes its canvas in pixels, so the stage is measured from its box and re-measured on
   * resize — the modal is responsive and a stage cannot be a percentage.
   *
   * ⚠ A CALLBACK ref, not `useRef` + an effect. This box does not exist until the photo has
   * loaded (the component renders a skeleton before that), so a mount effect ran while the ref
   * was still null: nothing was measured, nothing was observed, `box.width` stayed 0 and the live
   * stage was never rendered at all. A callback ref fires exactly when the node attaches.
   */
  const boxRef = useCallback(node => {
    observer.current?.disconnect();
    if (!node) return;
    const measure = () => setBox({ width: node.clientWidth, height: node.clientHeight });
    measure();
    observer.current = new ResizeObserver(measure);
    observer.current.observe(node);
  }, []);

  useEffect(() => () => observer.current?.disconnect(), []);

  /** Fit the whole photo in the viewport and centre it. Also the reset. */
  const fit = useCallback(() => {
    if (!image || !box.width || !box.height) return;
    const scale = Math.min(box.width / image.width, box.height / image.height);
    setView({ scale, x: (box.width - image.width * scale) / 2, y: (box.height - image.height * scale) / 2 });
  }, [image, box.width, box.height]);

  useEffect(fit, [fit]);
  useEffect(() => onDirtyChange?.(shapes.length > 0), [shapes, onDirtyChange]);

  /* The export renders from a SECOND stage that carries no view transform, so what is written is
     the photo at its own resolution with the marks where they were drawn — not a screenshot of
     the viewport. */
  useImperativeHandle(
    ref,
    () => ({
      hasMarks: () => shapes.length > 0,
      toDataURL: () => {
        /* With or without marks. The export IS the photo the corrective task carries, and gating it
           on marks left every un-annotated rejection — the common case — with a task and no photo
           (PICSELL-1615 follow-up). Null only while the photo has not loaded. */
        if (!exportStage.current || !image) return null;
        return exportStage.current.toDataURL({ pixelRatio: Math.min(1, MAX_EXPORT_WIDTH / (image?.width || 1)) });
      },
    }),
    [shapes, image]
  );

  /**
   * Pointer → image coordinates, CLAMPED into the photo.
   *
   * The viewport is bigger than the fitted photo — on a portrait shelf shot most of the canvas is
   * empty margin — and the layer is clipped to the image, so a mark made out there would be thrown
   * away. Ignoring those clicks outright was worse: it read as "the tool is broken". Clamping puts
   * the mark on the nearest edge of the photo, which always produces visible feedback.
   */
  const toImage = stage => {
    const point = stage.getPointerPosition();
    return [clamp((point.x - view.x) / view.scale, 0, image.width), clamp((point.y - view.y) / view.scale, 0, image.height)];
  };

  const onWheel = event => {
    event.evt.preventDefault();
    const stage = event.target.getStage();
    const pointer = stage.getPointerPosition();
    const next = clamp(view.scale * Math.exp(-event.evt.deltaY / 400), MIN_SCALE, MAX_SCALE);
    /* Keep the pixel under the cursor under the cursor — zooming about the centre makes finding
       a spot on a tall photo a hunt. */
    setView({
      scale: next,
      x: pointer.x - ((pointer.x - view.x) / view.scale) * next,
      y: pointer.y - ((pointer.y - view.y) / view.scale) * next,
    });
  };

  const onDown = event => {
    if (!image || tool === "pan") return;
    /* A mousedown on an existing text mark starts its drag (the node is draggable) — don't also
       drop a new text box under it. */
    if (event.target?.name?.() === "mark-text") return;
    const [x, y] = toImage(event.target.getStage());
    if (tool === "text") {
      /* `mousedown` fires BEFORE `blur`, so React has already replaced `typing` by the time the
         open box's onBlur runs — it would commit the new empty box and drop what was typed. */
      if (typing) commitText();
      setTyping({ x, y, value: "" });
      return;
    }
    drawing.current = true;
    setShapes(prev => [...prev, { tool, stroke, weight, points: [x, y, x, y] }]);
  };

  const onMove = event => {
    if (!drawing.current) return;
    const [x, y] = toImage(event.target.getStage());
    setShapes(prev => {
      const next = prev.slice();
      const shape = { ...next[next.length - 1] };
      shape.points = shape.tool === "pen" ? [...shape.points, x, y] : [shape.points[0], shape.points[1], x, y];
      next[next.length - 1] = shape;
      return next;
    });
  };

  const onUp = () => {
    if (!drawing.current) return;
    drawing.current = false;
    /* A click with no drag leaves a zero-length mark that draws as nothing but still counts as an
       edit — drop it so "has marks" stays truthful. */
    setShapes(prev => {
      const last = prev[prev.length - 1];
      if (!last || last.tool === "text") return prev;
      const [x1, y1, x2, y2] = ends(last.points);
      return Math.abs(x2 - x1) < 2 && Math.abs(y2 - y1) < 2 && last.points.length <= 4 ? prev.slice(0, -1) : prev;
    });
  };

  const commitText = () => {
    const value = typing?.value.trim();
    if (value) setShapes(prev => [...prev, { tool: "text", stroke, weight, text: value, points: [typing.x, typing.y, typing.x, typing.y] }]);
    setTyping(null);
  };

  /* The BOX's identity, not what is typed in it. Depending on the whole `typing` object re-ran the
     effect below on every keystroke, so `armed` was false for 60ms at a time and any blur-commit
     landing in that window was swallowed. */
  const typingKey = typing ? `${typing.x}:${typing.y}` : null;

  useEffect(() => {
    if (!typingKey) return undefined;
    armed.current = false;
    typingRef.current?.focus();
    const id = setTimeout(() => {
      armed.current = true;
    }, 60);
    return () => clearTimeout(id);
  }, [typingKey]);

  if (loading) return <Skeleton.Image active className="rtm-annot__skeleton" />;
  if (failed || !image) return <AntAlert type="warning" showIcon message={t("annot_unavailable")} />;

  return (
    <Flex vertical gap="small" className="rtm-annot">
      <Flex className="rtm-annot__bar" align="center" gap="small" wrap>
        {/* `options[].title` is NOT rendered by antd v6 — every item came back with no title and
            no accessible name, so an icon-only tool bar was unlabelled for a screen reader and
            unguessable for everyone else. The name goes inside `label` instead, which brings the
            tooltip with it. */}
        <Segmented
          value={tool}
          onChange={setTool}
          options={TOOLS.map(value => ({
            value,
            label: (
              <Tooltip title={t(`annot_${value}`)}>
                <span role="img" aria-label={t(`annot_${value}`)}>
                  {TOOL_ICON[value]}
                </span>
              </Tooltip>
            ),
          }))}
        />
        <Segmented
          value={weightKey}
          onChange={setWeightKey}
          options={Object.keys(WEIGHTS).map(value => ({
            value,
            label: <span aria-label={`${t("annot_weight")} ${t(`annot_weight_${value}`)}`}>{t(`annot_weight_${value}`)}</span>,
          }))}
        />
        <Space size={4} role="radiogroup" aria-label={t("annot_colour")}>
          {palette.map(value => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={value === stroke}
              aria-label={value}
              className={`rtm-annot__swatch${value === stroke ? " rtm-annot__swatch--on" : ""}`}
              style={{ background: value }}
              onClick={() => setColour(value)}
            />
          ))}
        </Space>
        <Space size={4} className="rtm-annot__acts">
          <Tooltip title={t("annot_fit")}>
            <Button size="small" icon={<AimOutlined />} aria-label={t("annot_fit")} onClick={fit} />
          </Tooltip>
          <Tooltip title={t("annot_undo")}>
            <Button
              size="small"
              icon={<UndoOutlined />}
              disabled={!shapes.length}
              aria-label={t("annot_undo")}
              onClick={() => setShapes(prev => prev.slice(0, -1))}
            />
          </Tooltip>
          <Tooltip title={t("annot_clear")}>
            <Button size="small" icon={<ClearOutlined />} disabled={!shapes.length} aria-label={t("annot_clear")} onClick={() => setShapes([])} />
          </Tooltip>
        </Space>
      </Flex>

      <div className={`rtm-annot__stage rtm-annot__stage--${tool}`} ref={boxRef}>
        {box.width > 0 && (
          <Stage
            width={box.width}
            height={box.height}
            onWheel={onWheel}
            onMouseDown={onDown}
            onMouseMove={onMove}
            onMouseUp={onUp}
            onMouseLeave={onUp}
            onTouchStart={onDown}
            onTouchMove={onMove}
            onTouchEnd={onUp}>
            <Layer
              scaleX={view.scale}
              scaleY={view.scale}
              x={view.x}
              y={view.y}
              /* Clipped to the photo, so a stroke that runs into the empty margin is cut where the
                 export would cut it. Without this the viewport shows marks the exported bitmap
                 does not have — the canvas is bigger than the fitted photo by design. */
              clipX={0}
              clipY={0}
              clipWidth={image.width}
              clipHeight={image.height}
              draggable={tool === "pan"}
              onDragEnd={event => {
                /* Konva bubbles a text mark's dragend up to here — ignore it, or the label's
                   coordinates would be written into the pan offset and the photo would jump.
                   Only the layer's own drag (panning) moves the view. */
                if (event.target !== event.currentTarget) return;
                setView(prev => ({ ...prev, x: event.target.x(), y: event.target.y() }));
              }}>
              <KonvaImage image={image} width={image.width} height={image.height} />
              <Marks
                shapes={shapes}
                onMoveText={(i, x, y) => setShapes(prev => prev.map((shape, idx) => (idx === i ? { ...shape, points: [x, y, x, y] } : shape)))}
              />
            </Layer>
          </Stage>
        )}

        {/* A real <input> over the canvas, placed where the click landed. Konva has no text entry;
            this is the standard pattern, and it means IME, autocorrect and RTL all just work. */}
        {typing && (
          <input
            ref={typingRef}
            className="rtm-annot__text"
            value={typing.value}
            placeholder={t("annot_text_hint")}
            style={{ left: typing.x * view.scale + view.x, top: typing.y * view.scale + view.y, color: stroke }}
            onChange={event => setTyping(prev => ({ ...prev, value: event.target.value }))}
            onBlur={() => {
              /* Ignore the focus-steal from the creating click; keep the box open so it can be typed
                 into. A genuine blur (clicking elsewhere) arrives after the box is armed. */
              if (!armed.current) {
                typingRef.current?.focus();
                return;
              }
              commitText();
            }}
            onKeyDown={event => {
              if (event.key === "Enter") commitText();
              if (event.key === "Escape") setTyping(null);
              event.stopPropagation();
            }}
          />
        )}
      </div>

      <Typography.Text type="secondary" className="rtm-annot__hint">
        {t("annot_hint")}
      </Typography.Text>

      {/* Off-screen and untransformed: the source of the exported bitmap. */}
      <div className="rtm-annot__export" aria-hidden="true">
        <Stage ref={exportStage} width={image.width} height={image.height}>
          <Layer>
            <KonvaImage image={image} width={image.width} height={image.height} />
            <Marks shapes={shapes} />
          </Layer>
        </Stage>
      </div>
    </Flex>
  );
}

export default forwardRef(PhotoAnnotator);
